/**
 * MessagesModal - Refactored main messaging modal
 * 
 * Modular architecture with extracted components:
 * - ThreadList: Thread listing with search and filters
 * - ThreadView: Individual thread conversation view
 * - MessageBubble: Individual message rendering
 * - MessageInput: Text input with attachments, voice, emoji
 * 
 * Hooks:
 * - useMessagesWebSocket: WebSocket connection management
 * - useThreads: Thread state management
 * - useMasterSync: Unified sync across all modules
 * 
 * Offline support:
 * - offlineMessages: Encrypted IndexedDB queue for offline messages
 * - unified offline store: AES-256-GCM encrypted storage
 * 
 * @author TotalAccess Development Team
 * @date January 2026
 */
'use client';

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, MessageSquare, UsersRound, MessageCircle, Users, CheckCircle2, ChevronLeft, RefreshCw } from 'lucide-react';
import { MOBILE_SAFE_AREA_TOP } from '@/components/work-items/mobileSafeArea';
import CapacitorModalFilterHeader, { ModalFilterBar } from '@/components/CapacitorModalFilterHeader';

// Components
import ThreadList from './ThreadList';
import ThreadView from './ThreadView';

// Hooks
import { useMessagesWebSocket } from './hooks/useMessagesWebSocket';
import type { WebSocketCallbacks } from './hooks/useMessagesWebSocket';
import { useThreads } from './hooks/useThreads';
import { useMasterSync } from '@/lib/useMasterSync';

// Types
import type { 
  ClientThread, 
  ThreadMessage, 
  Attachment, 
  IncomingCall, 
  ViewMode,
  EligibleMember
} from './types';

// Cloud Storage Upload
import { uploadToThreadStorage } from '@/lib/threadStorageUpload';

// Services
import offlineMessages from '@/lib/offlineMessages';
import {
  readCachedMessagingFeatureSettings,
  writeCachedMessagingFeatureSettings,
} from '@/lib/offline/messagingFeatureSettingsCache';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import { networkState } from '@/lib/networkState';
import { useBadge } from '@/contexts/BadgeContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useModalState } from '@/contexts/ModalStateContext';
import haptics from '@/lib/haptics';
import {
  createManagedTaskFromReference,
  hasStoredManagedTaskReference,
} from '@/lib/managedTaskReferences';
import {
  loadMessageSoundPreferences,
  playCategorySound,
  getThreadCategory
} from '@/lib/messageSoundPreferences';
import type { MessageSoundPreferences } from '@/lib/messageSoundPreferences';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { isBiometricLockActive, waitForBiometricUnlock } from '@/lib/biometricLockState';
import { usePendingMessage } from '@/lib/notificationStateManager';
import { getMessagingSharedLocation } from './locationSharing';
import {
  getAiOperationsManagerPromptFromMessage,
  isAiOperationsManagerThread,
} from '@/lib/operations/aiOperationsManagerConfig';
import {
  getAiProposalManagerPromptFromMessage,
  isAiProposalManagerThread,
} from '@/lib/operations/aiProposalManagerConfig';
import {
  AI_OPERATIONS_PARTICIPANT_OPTION,
  AI_PROPOSAL_PARTICIPANT_OPTION,
  type SpecialisedAiParticipantOption,
} from '@/lib/operations/specializedAiParticipants';

// User feature settings interface
interface UserFeatureSettings {
  message_centre: boolean;
  job_threads: boolean;
  client_thread: boolean;
  schedule_thread: boolean;
  show_when_online: boolean;
  voice_calls: boolean;
  video_calls: boolean;
  smart_task_field_worker: boolean;
  show_last_known_location: boolean;
  my_tasks: boolean;
  total_assistant: boolean;
}

// Shared content interface (from Android share intent)
export interface SharedContentData {
  type: 'text' | 'files';
  text?: string;
  files?: Array<{
    name: string;
    mimeType: string;
    size: number;
    data: string; // base64 encoded
  }>;
  timestamp?: number;
}

type OperationsEligibleMember = EligibleMember & {
  show_when_online?: boolean;
  last_activity_at?: string;
  has_active_session?: boolean;
  is_online?: boolean;
};

type PresenceAwareMember = Pick<OperationsEligibleMember, 'show_when_online' | 'last_activity_at' | 'has_active_session'> | null | undefined;
type HideNavigationWindow = Window & { __hideNavigationUI?: () => void };
type MessageReaction = NonNullable<ThreadMessage['reactions']>[number];
type OfflineQueueMessage = {
  thread_id: string;
  content: string;
  attachments?: Attachment[];
  reply_to_id?: string | null;
  temp_message_id: string;
  mentions?: number[];
};
type OperationsModuleUser = {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  display_name?: string;
};

type AiThreadContext = {
  prompt: string | null;
  shouldRespond: boolean;
  participant: SpecialisedAiParticipantOption;
};

const WORK_ITEM_UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
type ReferencePermalinkTarget = 'appointment' | 'job';

const APPOINTMENT_REFERENCE_MESSAGE_TYPES = new Set([
  'appointment_notification',
  'appointment_reminder'
]);
const JOB_REFERENCE_MESSAGE_TYPES = new Set([
  'job_assignment',
  'job_notification'
]);
const APPOINTMENT_REFERENCE_NOTIFICATION_TYPES = new Set([
  'appointment_scheduled',
  'appointment_reminder'
]);
const JOB_REFERENCE_NOTIFICATION_TYPES = new Set([
  'scheduled_job'
]);

function isGroupConversationThread(thread: ClientThread): boolean {
  return ['group', 'client_group', 'total_operations'].includes(thread.thread_type)
    && !thread.is_team_thread
    && thread.thread_type !== 'team';
}

function normalizeReferenceValue(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : null;
}

function isWorkItemUuid(value: unknown): value is string {
  return WORK_ITEM_UUID_REGEX.test(String(value || ''));
}

function getPrimaryReferencePermalinkTarget(message: ThreadMessage): ReferencePermalinkTarget | null {
  const metadata = message.metadata || {};
  const messageType = normalizeReferenceValue(message.message_type);
  const contentType = normalizeReferenceValue(message.content_type);
  const notificationType = normalizeReferenceValue(metadata.notification_type);
  const eventType = normalizeReferenceValue(metadata.event_type);
  const reviewScope = normalizeReferenceValue(metadata.review_scope);
  const referenceTypes = [messageType, contentType].filter((value): value is string => Boolean(value));
  const hasAppointmentReference = Boolean(normalizeReferenceValue(metadata.appointment_id));
  const hasJobReference = Boolean(
    normalizeReferenceValue(metadata.parent_job_id || metadata.job_id)
  );

  if (reviewScope === 'appointment' || reviewScope === 'job') {
    return reviewScope as ReferencePermalinkTarget;
  }

  if (referenceTypes.some((value) => APPOINTMENT_REFERENCE_MESSAGE_TYPES.has(value))) {
    return 'appointment';
  }

  if (
    notificationType &&
    APPOINTMENT_REFERENCE_NOTIFICATION_TYPES.has(notificationType)
  ) {
    return 'appointment';
  }

  if (referenceTypes.some((value) => JOB_REFERENCE_MESSAGE_TYPES.has(value))) {
    return 'job';
  }

  if (notificationType && JOB_REFERENCE_NOTIFICATION_TYPES.has(notificationType)) {
    return 'job';
  }

  if (eventType === 'ai_operations_review_flagged') {
    if (hasAppointmentReference) {
      return 'appointment';
    }

    if (hasJobReference) {
      return 'job';
    }
  }

  if (hasAppointmentReference) {
    return 'appointment';
  }

  if (hasJobReference) {
    return 'job';
  }

  return null;
}

function appendPermalinkIfMissing(content: string, label: string, url?: string | null): string {
  if (!url || content.includes(url)) {
    return content;
  }

  const trimmed = content.replace(/\s+$/, '');
  const separator = trimmed.length > 0 ? '\n\n' : '';
  return `${trimmed}${separator}${label}: ${url}`;
}

function removePermalinkIfPresent(content: string, label: string, url?: string | null): string {
  if (!url || !content.includes(url)) {
    return content;
  }

  let nextContent = content;
  const variants = [
    `\n\n${label}: ${url}`,
    `\n${label}: ${url}`,
    `${label}: ${url}`,
  ];

  for (const variant of variants) {
    nextContent = nextContent.split(variant).join('');
  }

  return nextContent.replace(/\n{3,}/g, '\n\n').trimEnd();
}

function getThreadMessageIdentifier(message: Partial<ThreadMessage> & { message_id?: string }): string | null {
  const rawId = message._id || message.id || message.message_id || message.temp_id;
  return rawId ? String(rawId) : null;
}

// Helper to check if a member is online (respects show_when_online preference)
function isMemberOnline(member: PresenceAwareMember): boolean {
  if (!member) return false;
  if (member.show_when_online === false) return false;
  if (!member.last_activity_at) return false;
  
  const lastActive = new Date(member.last_activity_at).getTime();
  const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
  return lastActive > fiveMinutesAgo;
}

// Helper to get last seen text (matches OperationsCallModal format)
function getMemberLastSeen(member: PresenceAwareMember): string | null {
  if (!member) return null;
  if (member.show_when_online === false) return null;
  if (member.has_active_session === false) return 'Offline';
  if (!member.last_activity_at) {
    return member.has_active_session ? 'Available' : null;
  }
  
  try {
    const lastActive = new Date(member.last_activity_at);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - lastActive.getTime()) / (1000 * 60));
    
    if (diffMinutes <= 5) return 'Online';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    
    const sastTime = lastActive.toLocaleString('en-ZA', { 
      timeZone: 'Africa/Johannesburg',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return sastTime;
  } catch {
    return null;
  }
}

// LiveKit (for voice/video calls) - Calls now use OperationsCallModal
// import { Room, RoomEvent, createLocalTracks } from 'livekit-client';

interface MessagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShowDashboard?: () => void;
  isFullPage?: boolean;
  initialThreadId?: string;
  initialJobId?: number;
  initialScheduleId?: number;
  initialDirectUserId?: number;
  pendingSharedContent?: SharedContentData | null;
  onSharedContentHandled?: () => void;
}

export default function MessagesModal({
  isOpen,
  onClose,
  onShowDashboard,
  isFullPage = false,
  initialThreadId,
  initialJobId,
  initialScheduleId,
  initialDirectUserId,
  pendingSharedContent,
  onSharedContentHandled,
}: MessagesModalProps) {
  const { user, client, isAuthenticated } = useAuth();
  const { refreshBadgeCounts } = useBadge();
  const { currentTheme, clientBranding } = useTheme();
  const { registerBackHandler, unregisterBackHandler } = useModalState();
  const primaryColor = clientBranding?.primary_color || currentTheme?.colors?.primary || '#3b82f6';
  const effectiveUserId = user?.id ?? authService.getUser()?.id;
  const hasAuthenticatedSession = isAuthenticated || authService.isAuthenticated();
  
  // Use theme context for proper dark mode detection
  const isDark = currentTheme?.mode === 'dark';
  const isMobileApp = Capacitor.isNativePlatform();
  const messagesHeaderActionClass = `inline-flex items-center justify-center rounded-lg border p-2 transition-colors ${
    isDark
      ? 'border-gray-700 bg-gray-900/50 text-gray-500 hover:border-gray-600 hover:text-gray-300'
      : 'border-gray-200 bg-white text-gray-400 hover:border-gray-300 hover:text-gray-600'
  }`;

  // On Capacitor, show main Header when modal opens
  useEffect(() => {
    if (isOpen && isMobileApp) {
      window.dispatchEvent(new Event('showHeader'));
    }
  }, [isOpen, isMobileApp]);
  
  // MasterSync for unified sync across all modules
  const {
    isOnline: masterSyncOnline,
    triggerSync: triggerMasterSync,
  } = useMasterSync({
    autoSyncOnReconnect: true,
    backgroundSyncInterval: 5 * 60 * 1000 // 5 minutes
  });
  
  // Sound preferences ref
  const soundPrefsRef = useRef<MessageSoundPreferences>(loadMessageSoundPreferences());
  
  // Track if we've already opened the initial thread (to avoid re-opening on re-renders)
  const initialThreadOpenedRef = useRef<string | null>(null);
  const initialDirectUserHandledRef = useRef<number | null>(null);
  const httpThreadsFallbackAttemptedRef = useRef(false);
  
  // Cold start notification handling - FCM message tap during cold start
  const { pendingMessage, confirmHandled: confirmMessageHandled } = usePendingMessage();
  
  // User feature settings (for show_when_online etc.)
  const [userFeatureSettings, setUserFeatureSettings] = useState<UserFeatureSettings | null>(null);
  
  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [showNewThreadModal, setShowNewThreadModal] = useState(false);
  
  // Thread category filter (All, Direct, Groups, Teams)
  type ModalThreadCategory = 'all' | 'direct' | 'groups' | 'teams';
  const [threadCategory, setThreadCategory] = useState<ModalThreadCategory>('all');
  
  // Loading states
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sharingLocation, setSharingLocation] = useState(false);
  const [aiParticipantThinkingThreadId, setAiParticipantThinkingThreadId] = useState<string | null>(null);
  const [resolvedWorkItemPermalinks, setResolvedWorkItemPermalinks] = useState<Record<string, string>>({});
  
  // Share mode - when user is sharing content from another app
  const [isShareMode, setIsShareMode] = useState(false);
  const [shareAttachments, setShareAttachments] = useState<Attachment[]>([]);
  const [shareText, setShareText] = useState<string>('');
  const [addingManagedMessageTaskId, setAddingManagedMessageTaskId] = useState<string | null>(null);
  const [managedMessageTaskIds, setManagedMessageTaskIds] = useState<Record<string, true>>({});
  const shareHandledRef = useRef(false);
  
  // Offline state
  const [isOnline, setIsOnline] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  
  // Operations users for new thread (fetched from module-access API like OperationsCallModal)
  const [operationsUsers, setOperationsUsers] = useState<OperationsEligibleMember[]>([]);
  const [loadingOperationsUsers, setLoadingOperationsUsers] = useState(false);
  
  // WebSocket callbacks reference
  const callbacksRef = useRef<WebSocketCallbacks>({});
  const pendingWorkItemPermalinksRef = useRef<Set<string>>(new Set());
  const syncOfflineMessagesRef = useRef<() => Promise<void>>(async () => {});
  
  // Initialize threads hook
  const {
    threads,
    searchQuery,
    setSearchQuery,
    setSearchResults,
    isSearching,
    sortBy,
    setSortBy,
    sortedThreads,
    selectedThread,
    messages,
    loading,
    totalClientsInThreads,
    currentTypingUsers,
    pinnedMentions,
    selectThread,
    setSelectedThread,
    setMessages,
    handleThreadsList,
    handleSearchResults,
    handleThreadMessages,
    handleThreadDeleted,
    handleNewMessage,
    handleMessageSent,
    handleMessageDeleted,
    handleReactionUpdated,
    handleUserTyping,
    handleBadgeCountsUpdated,
    handleEligibleMembers,
    handleMemberAdded,
    handleThreadCreated,
    addOptimisticMessage,
    updateMessageStatus: updateOptimisticMessage,
    dismissMention,
    dismissAllMentions,
    fetchThreadsHTTP,
    fetchMessagesHTTP
  } = useThreads();
  
  // Set up WebSocket callbacks
  useEffect(() => {
    Object.assign(callbacksRef.current, {
      onThreadsList: handleThreadsList,
      onSearchResults: handleSearchResults,
      onThreadMessages: handleThreadMessages,
      onNewMessage: (threadId: string, message: ThreadMessage) => {
        // Play sound and haptic if message is from someone else
        if (message.sender_user_id !== user?.id) {
          // Get the thread to determine category
          const thread = threads.find(t => t.thread_id === threadId);
          const category = getThreadCategory({
            threadType: thread?.thread_type,
            memberCount: thread?.member_count ?? thread?.members?.length,
            isSystem: thread?.thread_type === 'system'
          });
          playCategorySound(soundPrefsRef.current, category);
          
          // Haptic feedback for incoming message
          haptics.light();
          
          // Refresh badge counts
          refreshBadgeCounts?.();
        }
        handleNewMessage(threadId, message);
      },
      onMessageSent: handleMessageSent,
      onMessageDeleted: handleMessageDeleted,
      onReactionUpdated: handleReactionUpdated,
      onUserTyping: handleUserTyping,
      onBadgeCountsUpdated: (threadId: string, unreadCount: number, lastMessage?: ThreadMessage | null) => {
        handleBadgeCountsUpdated(threadId, unreadCount, lastMessage);
        // Refresh global badge counts
        refreshBadgeCounts?.();
      },
      onEligibleMembers: handleEligibleMembers,
      onMemberAdded: handleMemberAdded,
      onThreadCreated: handleThreadCreated,
      onThreadDeleted: (threadId: string) => {
        handleThreadDeleted(threadId);
        haptics.warning();
        notify.success('Thread removed successfully');
      },
      onError: (error: string) => {
        if (!error) return; // Ignore empty/undefined errors
        console.error('[MessagesModal] WebSocket error:', error);
        haptics.error();
        // Use short duration to prevent UI blocking
        // Thread not found errors should navigate back to thread list
        notify.error(error, 3000);
        
        // If thread not found, reset to thread list view
        if (error?.toLowerCase().includes('thread not found')) {
          selectThread(null);
          setViewMode('list');
          // Clear the initial thread ref so we don't retry
          initialThreadOpenedRef.current = null;
        }
      },
      onConnected: () => {
        console.log('[MessagesModal] WebSocket connected');
        // Sync offline messages when reconnected
        void syncOfflineMessagesRef.current();
      },
      onDisconnected: () => {
        console.log('[MessagesModal] WebSocket disconnected');
      }
    });
  }, [
    handleThreadsList, handleSearchResults, handleThreadMessages, handleThreadDeleted, handleNewMessage, handleMessageSent, handleMessageDeleted,
    handleReactionUpdated, handleUserTyping, handleBadgeCountsUpdated, handleEligibleMembers,
    handleMemberAdded, handleThreadCreated, selectThread, user?.id, threads, refreshBadgeCounts
  ]);
  
  // Initialize WebSocket connection
  const {
    isConnected,
    connectionStatus,
    send,
    getThreads,
    joinThread,
    getThreadMessages,
    sendMessage,
    createThread,
    addMember,
    sendTyping,
    markAsRead,
    updateViewingStatus
  } = useMessagesWebSocket({
    enabled: isOpen,
    userId: effectiveUserId,
    isAuthenticated: hasAuthenticatedSession,
    isOpen,
    callbacks: callbacksRef.current
  });
  
  // Update viewing status when modal opens/closes or thread changes
  useEffect(() => {
    if (isConnected) {
      if (isOpen && selectedThread) {
        // User is viewing a specific thread
        updateViewingStatus(true, selectedThread.thread_id);
      } else if (isOpen) {
        // User has messages modal open but not viewing a specific thread
        updateViewingStatus(true, null);
      } else {
        // Messages modal is closed
        updateViewingStatus(false, null);
      }
    }
    
    // Cleanup: mark as not viewing when component unmounts
    return () => {
      if (isConnected) {
        updateViewingStatus(false, null);
      }
    };
  }, [isOpen, selectedThread, isConnected, updateViewingStatus]);
  
  // Monitor online status
  useEffect(() => {
    const checkOnline = () => setIsOnline(networkState.isOnline());
    checkOnline();
    
    window.addEventListener('online', checkOnline);
    window.addEventListener('offline', checkOnline);
    
    return () => {
      window.removeEventListener('online', checkOnline);
      window.removeEventListener('offline', checkOnline);
    };
  }, []);
  
  // Mobile: Listen for app resume to sync messages
  // When user returns from background, trigger a sync
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    let resumeListener: Awaited<ReturnType<typeof App.addListener>> | null = null;
    let isDisposed = false;
    
    const setupResumeListener = async () => {
      resumeListener = await App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive && isOpen) {
          if (isBiometricLockActive()) {
            console.log('[MessagesModal] Resume sync deferred until biometric unlock');
            const unlocked = await waitForBiometricUnlock();
            if (!unlocked || isDisposed) {
              return;
            }
          }

          console.log('[MessagesModal] App resumed - triggering sync');
          
          // Trigger MasterSync for unified sync across all modules
          if (masterSyncOnline) {
            triggerMasterSync();
          }
          
          // Refresh badge counts
          refreshBadgeCounts?.();
        }
      });
    };
    
    setupResumeListener();
    
    return () => {
      isDisposed = true;
      if (resumeListener) {
        resumeListener.remove();
      }
    };
  }, [isOpen, masterSyncOnline, triggerMasterSync, refreshBadgeCounts]);
  
  // Listen for deepLinkSyncConnect event (triggered after external app operations)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    const handleSyncConnect = async (event: Event) => {
      const detail = (event as CustomEvent).detail;
      console.log('[MessagesModal] 🔄 Received syncConnect deep link:', detail);
      
      // Trigger MasterSync for unified sync
      if (masterSyncOnline) {
        await triggerMasterSync();
      }
      
      // Refresh badge counts
      refreshBadgeCounts?.();
      
      notify.success('Messages synced');
    };
    
    // Listen for both event names for compatibility
    window.addEventListener('syncConnect', handleSyncConnect);
    window.addEventListener('deepLinkSyncConnect', handleSyncConnect);
    
    return () => {
      window.removeEventListener('syncConnect', handleSyncConnect);
      window.removeEventListener('deepLinkSyncConnect', handleSyncConnect);
    };
  }, [masterSyncOnline, triggerMasterSync, refreshBadgeCounts]);
  
  // Filter threads by selected category
  const filteredThreads = useMemo(() => {
    switch (threadCategory) {
      case 'direct':
        return sortedThreads.filter(t => t.thread_type === 'direct');
      case 'groups':
        return sortedThreads.filter(isGroupConversationThread);
      case 'teams':
        return sortedThreads.filter(t => t.thread_type === 'team' || t.is_team_thread);
      default:
        return sortedThreads;
    }
  }, [sortedThreads, threadCategory]);
  
  // Category counts for filter tabs
  const categoryCounts = useMemo(() => ({
    all: sortedThreads.length,
    direct: sortedThreads.filter(t => t.thread_type === 'direct').length,
    groups: sortedThreads.filter(isGroupConversationThread).length,
    teams: sortedThreads.filter(t => t.thread_type === 'team' || t.is_team_thread).length,
  }), [sortedThreads]);

  const messagingRealtimeStatus: 'live' | 'online' | 'offline' = isConnected
    ? 'live'
    : isOnline
      ? 'online'
      : 'offline';
  const messagingLiveConnected = messagingRealtimeStatus === 'live';
  const messagingStatusSummary = isSearching
    ? `${filteredThreads.length} search results`
    : `${filteredThreads.length} conversation${filteredThreads.length !== 1 ? 's' : ''}`;

  const handleThreadSearchChange = useCallback((query: string) => {
    setSearchResults(null);
    setSearchQuery(query);
  }, [setSearchQuery, setSearchResults]);

  const handleClearThreadSearch = useCallback(() => {
    setSearchResults(null);
    setSearchQuery('');
  }, [setSearchQuery, setSearchResults]);

  const handleMessagesRefresh = useCallback(async () => {
    if (masterSyncOnline) {
      await triggerMasterSync();
    }
    await fetchThreadsHTTP();
    refreshBadgeCounts?.();
  }, [fetchThreadsHTTP, masterSyncOnline, refreshBadgeCounts, triggerMasterSync]);

  const threadCategoryFilter = useMemo(
    () => ({
      value: threadCategory,
      onChange: (key: string) => setThreadCategory(key as ModalThreadCategory),
      options: [
        { key: 'all', label: 'All', icon: MessageSquare, count: categoryCounts.all },
        { key: 'direct', label: 'Direct', icon: MessageCircle, count: categoryCounts.direct },
        { key: 'groups', label: 'Groups', icon: Users, count: categoryCounts.groups },
        { key: 'teams', label: 'Teams', icon: UsersRound, count: categoryCounts.teams },
      ],
    }),
    [categoryCounts, threadCategory],
  );

  const sendMessageHTTP = useCallback(async (
    threadId: string,
    content: string,
    options?: {
      attachments?: Attachment[];
      replyTo?: { message_id: string; content: string; sender_name: string };
      mentions?: number[];
      messageType?: string;
      tempId?: string;
      location?: { latitude: number; longitude: number; accuracy?: number; address?: string };
    }
  ) => {
    const accessToken = authService.getTokens()?.access_token;
    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`/api/v1/messaging/threads/${encodeURIComponent(threadId)}/messages`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        content,
        message_type: options?.messageType || (options?.attachments?.length ? 'attachment' : 'text'),
        attachments: options?.attachments || [],
        reply_to: options?.replyTo?.message_id,
        mentions: options?.mentions || [],
        location: options?.location,
      }),
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result?.success || !result?.message) {
      throw new Error(result?.error || 'Failed to send message');
    }

    const message = {
      ...result.message,
      temp_id: result.message?.temp_id || options?.tempId,
    } as ThreadMessage;

    handleMessageSent(threadId, message);
    return message;
  }, [handleMessageSent]);

  useEffect(() => {
    if (!isOpen || messages.length === 0) return;

    const accessToken = authService.getTokens()?.access_token;
    if (!accessToken) return;

    const pendingIds: string[] = [];

    for (const message of messages) {
      const metadata = (message.metadata || {}) as Record<string, unknown>;
      const primaryReferenceTarget = getPrimaryReferencePermalinkTarget(message);
      const appointmentId = primaryReferenceTarget === 'appointment'
        ? normalizeReferenceValue(metadata.appointment_id)
        : null;
      const jobReferenceId = primaryReferenceTarget === 'job'
        ? normalizeReferenceValue(metadata.parent_job_id || metadata.job_id)
        : null;

      if (
        appointmentId &&
        isWorkItemUuid(appointmentId) &&
        !metadata.appointment_permalink_url &&
        !resolvedWorkItemPermalinks[appointmentId] &&
        !pendingWorkItemPermalinksRef.current.has(appointmentId)
      ) {
        pendingIds.push(appointmentId);
      }

      if (
        jobReferenceId &&
        isWorkItemUuid(jobReferenceId) &&
        !metadata.parent_job_permalink_url &&
        !metadata.job_permalink_url &&
        !resolvedWorkItemPermalinks[jobReferenceId] &&
        !pendingWorkItemPermalinksRef.current.has(jobReferenceId)
      ) {
        pendingIds.push(jobReferenceId);
      }
    }

    const uniquePendingIds = Array.from(new Set(pendingIds));
    if (uniquePendingIds.length === 0) {
      return;
    }

    let cancelled = false;
    uniquePendingIds.forEach((id) => pendingWorkItemPermalinksRef.current.add(id));

    void (async () => {
      try {
        const resolutions = await Promise.all(uniquePendingIds.map(async (workItemId) => {
          try {
            const response = await fetch(`/api/v1/work-items/${workItemId}/permalink`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
            });

            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result?.success || !result?.url) {
              return null;
            }

            return [workItemId, result.url] as const;
          } catch (error) {
            console.warn('[MessagesModal] Failed to resolve work item permalink:', workItemId, error);
            return null;
          }
        }));

        if (cancelled) return;

        const nextEntries = resolutions.filter((entry): entry is readonly [string, string] => Boolean(entry));
        if (nextEntries.length > 0) {
          setResolvedWorkItemPermalinks((prev) => {
            const updated = { ...prev };
            nextEntries.forEach(([id, url]) => {
              updated[id] = url;
            });
            return updated;
          });
        }
      } finally {
        uniquePendingIds.forEach((id) => pendingWorkItemPermalinksRef.current.delete(id));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, messages, resolvedWorkItemPermalinks]);

  useEffect(() => {
    if (!isOpen || isConnected || !selectedThread) {
      return;
    }

    const threadId = selectedThread.id || selectedThread.thread_id;
    if (!threadId) {
      return;
    }

    void fetchMessagesHTTP(threadId);
  }, [fetchMessagesHTTP, isConnected, isOpen, selectedThread]);

  const threadMessagesWithPermalinks = useMemo(() => messages.map((message) => {
    const metadata = (message.metadata || {}) as Record<string, unknown>;
    const primaryReferenceTarget = getPrimaryReferencePermalinkTarget(message);
    const appointmentId = primaryReferenceTarget === 'appointment'
      ? normalizeReferenceValue(metadata.appointment_id)
      : null;
    const jobReferenceId = primaryReferenceTarget === 'job'
      ? normalizeReferenceValue(metadata.parent_job_id || metadata.job_id)
      : null;

    const appointmentPermalink = normalizeReferenceValue(metadata.appointment_permalink_url)
      || (appointmentId ? resolvedWorkItemPermalinks[appointmentId] : undefined)
      || null;
    const jobPermalink = normalizeReferenceValue(metadata.parent_job_permalink_url)
      || normalizeReferenceValue(metadata.job_permalink_url)
      || (jobReferenceId ? resolvedWorkItemPermalinks[jobReferenceId] : undefined)
      || null;

    const originalContent = typeof message.content === 'string'
      ? message.content
      : String(message.content || '');
    let nextContent = removePermalinkIfPresent(originalContent, 'Open appointment', appointmentPermalink);
    nextContent = removePermalinkIfPresent(nextContent, 'Open job', jobPermalink);

    if (primaryReferenceTarget === 'appointment' && appointmentPermalink) {
      nextContent = appendPermalinkIfMissing(nextContent, 'Open appointment', appointmentPermalink);
    }

    if (primaryReferenceTarget === 'job' && jobPermalink) {
      nextContent = appendPermalinkIfMissing(nextContent, 'Open job', jobPermalink);
    }

    const nextMetadata: Record<string, unknown> = {
      ...metadata,
      ...(primaryReferenceTarget === 'appointment' && appointmentPermalink
        ? { appointment_permalink_url: appointmentPermalink }
        : {}),
      ...(primaryReferenceTarget === 'job' && jobPermalink
        ? metadata.parent_job_id
          ? { parent_job_permalink_url: jobPermalink }
          : { job_permalink_url: jobPermalink }
        : {})
    };

    const contentChanged = nextContent !== originalContent;
    const metadataChanged =
      nextMetadata.appointment_permalink_url !== metadata.appointment_permalink_url ||
      nextMetadata.parent_job_permalink_url !== metadata.parent_job_permalink_url ||
      nextMetadata.job_permalink_url !== metadata.job_permalink_url;

    if (!contentChanged && !metadataChanged) {
      return message;
    }

    return {
      ...message,
      content: nextContent,
      metadata: nextMetadata,
    };
  }), [messages, resolvedWorkItemPermalinks]);

  useEffect(() => {
    const storedIds = threadMessagesWithPermalinks.reduce<Record<string, true>>((acc, message) => {
      const messageId = getThreadMessageIdentifier(message as ThreadMessage & { message_id?: string });
      if (messageId && hasStoredManagedTaskReference('thread_message', messageId)) {
        acc[messageId] = true;
      }
      return acc;
    }, {});

    if (Object.keys(storedIds).length > 0) {
      setManagedMessageTaskIds((prev) => ({ ...prev, ...storedIds }));
    }
  }, [threadMessagesWithPermalinks]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleManagedTaskReferenceAdded = (event: Event) => {
      const detail = (event as CustomEvent<{ referenceType?: string; referenceId?: string }>).detail;
      if (detail?.referenceType === 'thread_message' && detail.referenceId) {
        setManagedMessageTaskIds((prev) => ({
          ...prev,
          [String(detail.referenceId)]: true,
        }));
      }
    };

    window.addEventListener('managedTaskReferenceAdded', handleManagedTaskReferenceAdded as EventListener);
    return () => {
      window.removeEventListener('managedTaskReferenceAdded', handleManagedTaskReferenceAdded as EventListener);
    };
  }, []);

  const hasManagedMessageTask = useCallback((messageId: string) => (
    Boolean(managedMessageTaskIds[messageId]) || hasStoredManagedTaskReference('thread_message', messageId)
  ), [managedMessageTaskIds]);

  const handleAddMessageToManagedTasks = useCallback(async (messageId: string) => {
    if (!selectedThread || !effectiveUserId) {
      return;
    }

    if (hasManagedMessageTask(messageId) || addingManagedMessageTaskId === messageId) {
      notify.info('Message is already in managed tasks', 2500);
      return;
    }

    const targetMessage = threadMessagesWithPermalinks.find((message) => {
      const candidateId = getThreadMessageIdentifier(message as ThreadMessage & { message_id?: string });
      return candidateId === messageId;
    });

    const threadIdentifier = selectedThread.thread_id || selectedThread.id;
    const threadLabel = selectedThread.thread_name || selectedThread.title || 'Conversation';
    const senderLabel = targetMessage?.sender_name?.trim() || threadLabel;
    const preview = typeof targetMessage?.content === 'string'
      ? targetMessage.content.trim().slice(0, 280)
      : '';
    const descriptionParts = [preview, `Thread: ${threadLabel}`].filter(Boolean);

    try {
      setAddingManagedMessageTaskId(messageId);
      const result = await createManagedTaskFromReference({
        referenceType: 'thread_message',
        referenceId: messageId,
        referenceUrl: threadIdentifier
          ? `/client-zone/modules/total-connect?thread=${encodeURIComponent(threadIdentifier)}`
          : undefined,
        title: `Follow up: ${senderLabel}`,
        description: descriptionParts.join('\n\n'),
        priority: 'medium',
        assignedTo: effectiveUserId,
        contextData: {
          source_module: 'messaging',
          thread_id: threadIdentifier,
          thread_name: threadLabel,
          message_id: messageId,
          message_sender: targetMessage?.sender_name || null,
          message_timestamp: targetMessage?.timestamp || targetMessage?.created_at || null,
        },
      });

      setManagedMessageTaskIds((prev) => ({
        ...prev,
        [messageId]: true,
      }));

      notify[result.alreadyExisted ? 'info' : 'success'](
        result.alreadyExisted ? 'Message is already in managed tasks' : 'Message added to managed tasks',
        2500,
      );
    } catch (error) {
      console.error('[MessagesModal] Failed to add message to managed tasks:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to add message to managed tasks', 3000);
    } finally {
      setAddingManagedMessageTaskId((current) => current === messageId ? null : current);
    }
  }, [addingManagedMessageTaskId, effectiveUserId, hasManagedMessageTask, selectedThread, threadMessagesWithPermalinks]);
  
  // Operations feature settings — hydrate from IndexedDB first, then refresh from API
  useEffect(() => {
    let cancelled = false;
    const clientId = Number(client?.id || 0);

    const run = async () => {
      if (!user?.id || !clientId) {
        return;
      }

      try {
        const cached = await readCachedMessagingFeatureSettings(clientId);
        if (!cancelled && cached?.feature_settings) {
          setUserFeatureSettings(cached.feature_settings as unknown as UserFeatureSettings);
        }
      } catch {
        // ignore cache errors
      }

      try {
        const tokens = authService.getTokens();
        if (!tokens?.access_token) {
          return;
        }

        const response = await fetch('/api/v1/operations/my-features', {
          headers: {
            Authorization: `Bearer ${tokens.access_token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!cancelled && response.ok) {
          const data = await response.json();
          if (data.success && data.feature_settings) {
            setUserFeatureSettings(data.feature_settings);
            await writeCachedMessagingFeatureSettings(clientId, data.feature_settings);
          }
        }
      } catch (err) {
        console.error('[MessagesModal] Error fetching feature settings:', err);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [user?.id, client?.id]);
  
  // Keep sound preferences updated
  useEffect(() => {
    const refresh = () => {
      soundPrefsRef.current = loadMessageSoundPreferences();
    };
    
    refresh();
    window.addEventListener('ta:messageSoundPreferencesChanged', refresh);
    return () => window.removeEventListener('ta:messageSoundPreferencesChanged', refresh);
  }, []);
  
  // Handle pending shared content from Android share intent
  useEffect(() => {
    if (!pendingSharedContent || !isOpen || shareHandledRef.current) return;
    
    console.log('[MessagesModal] 📤 Processing shared content:', pendingSharedContent.type);
    shareHandledRef.current = true;
    const sharedFiles = pendingSharedContent.files;
    
    // Convert shared content to attachments
    if (pendingSharedContent.type === 'files' && sharedFiles && sharedFiles.length > 0) {
      const attachments: Attachment[] = sharedFiles.map((file, index) => {
        const isImage = file.mimeType?.startsWith('image/') || false;
        const isVideo = file.mimeType?.startsWith('video/') || false;
        return {
          id: `share-${Date.now()}-${index}`,
          filename: file.name,
          originalFilename: file.name,
          url: `data:${file.mimeType};base64,${file.data}`,
          contentType: file.mimeType,
          mimeType: file.mimeType,
          size: file.size,
          isImage,
          isVideo
        };
      });
      
      setShareAttachments(attachments);
      setIsShareMode(true);
      setViewMode('list'); // Show thread list so user can pick a thread
      
      console.log('[MessagesModal] 📤 Share mode enabled with', attachments.length, 'files');
    } else if (pendingSharedContent.type === 'text' && pendingSharedContent.text) {
      setShareText(pendingSharedContent.text);
      setIsShareMode(true);
      setViewMode('list');
      
      console.log('[MessagesModal] 📤 Share mode enabled with text');
    }
    
    // Notify parent that we've handled the content
    onSharedContentHandled?.();
  }, [pendingSharedContent, isOpen, onSharedContentHandled]);
  
  // Reset share mode when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsShareMode(false);
      setShareAttachments([]);
      setShareText('');
      shareHandledRef.current = false;
    }
  }, [isOpen]);
  
  // Handle pending message from cold start (FCM notification tap)
  // This uses the unified NotificationStateManager for persistence across cold starts
  const pendingMessageHandledRef = useRef<string | null>(null);
  useEffect(() => {
    if (pendingMessage?.threadId && isOpen && isConnected && pendingMessageHandledRef.current !== pendingMessage.threadId) {
      // Prevent duplicate handling
      pendingMessageHandledRef.current = pendingMessage.threadId;
      
      console.log('[MessagesModal] 💬 Processing pending message from notification:', pendingMessage.threadId);
      
      // Try to find the thread in the loaded list
      const thread = threads.find(t => t.id === pendingMessage.threadId || t.thread_id === pendingMessage.threadId);
      
      if (thread) {
        // Found the thread - select it
        selectThread(thread);
        joinThread(pendingMessage.threadId);
        getThreadMessages(pendingMessage.threadId);
        setViewMode('thread');
        markAsRead(pendingMessage.threadId);
        
        // Clear the FCM notification for this thread
        import('@/lib/capacitorDeviceService').then(({ capacitorDeviceService }) => {
          capacitorDeviceService.cancelNotificationForThread(pendingMessage.threadId).catch(() => {});
        }).catch(() => {});
      } else if (threads.length > 0) {
        // Thread not in list but we have threads loaded - try to join directly
        joinThread(pendingMessage.threadId);
        getThreadMessages(pendingMessage.threadId);
        setViewMode('thread');
        markAsRead(pendingMessage.threadId);
        
        // Clear the FCM notification for this thread
        import('@/lib/capacitorDeviceService').then(({ capacitorDeviceService }) => {
          capacitorDeviceService.cancelNotificationForThread(pendingMessage.threadId).catch(() => {});
        }).catch(() => {});
      }
      
      // Confirm we handled it (clears from storage)
      confirmMessageHandled(pendingMessage.threadId);
      
      // Hide cold start navigation UI
      if (typeof window !== 'undefined') {
        (window as HideNavigationWindow).__hideNavigationUI?.();
      }
    }
  }, [pendingMessage, isOpen, isConnected, threads, selectThread, joinThread, getThreadMessages, markAsRead, confirmMessageHandled]);
  
  // Update offline message counts
  useEffect(() => {
    const updateCounts = async () => {
      const pending = await offlineMessages.getPendingCount();
      const failed = await offlineMessages.getFailedCount();
      setPendingCount(pending);
      setFailedCount(failed);
    };
    
    updateCounts();
    const interval = setInterval(updateCounts, 5000);
    return () => clearInterval(interval);
  }, []);

  // Reset HTTP fallback when the modal closes so the next open can retry
  useEffect(() => {
    if (!isOpen) {
      httpThreadsFallbackAttemptedRef.current = false;
    }
  }, [isOpen]);

  // Fallback to the HTTP thread list endpoint if the thread list is still empty (WS slow or stale cache)
  useEffect(() => {
    if (!isOpen || threads.length > 0 || httpThreadsFallbackAttemptedRef.current) {
      return;
    }

    const fallbackTimer = window.setTimeout(() => {
      if (!isOpen || threads.length > 0 || httpThreadsFallbackAttemptedRef.current) {
        return;
      }

      httpThreadsFallbackAttemptedRef.current = true;
      console.log('[MessagesModal] Thread list still empty - fetching HTTP fallback');
      void fetchThreadsHTTP();
    }, 1200);

    return () => window.clearTimeout(fallbackTimer);
  }, [isOpen, threads.length, fetchThreadsHTTP]);

  const invokeAiThreadParticipant = useCallback(async (
    thread: ClientThread,
    prompt: string,
    participant: SpecialisedAiParticipantOption,
  ) => {
    const threadId = thread.id || thread.thread_id;
    if (!threadId) return;

    const sendParticipantRequest = async (accessToken: string) => fetch(participant.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        thread_id: threadId,
        team_id: thread.team_id || null,
        solution_key: thread.solution_key || undefined,
        prompt,
      }),
    });

    let accessToken = authService.getTokens()?.access_token;
    if (!accessToken) {
      notify.error(`You need to be signed in to use ${participant.senderName}`, 3000);
      return;
    }

    setAiParticipantThinkingThreadId(threadId);

    try {
      let response = await sendParticipantRequest(accessToken);

      if (response.status === 401) {
        const refreshed = await authService.refreshToken();
        if (!refreshed) {
          throw new Error(`Your session expired before ${participant.senderName} could respond. Please sign in again.`);
        }

        accessToken = authService.getTokens()?.access_token;
        if (!accessToken) {
          throw new Error(`Your session expired before ${participant.senderName} could respond. Please sign in again.`);
        }

        response = await sendParticipantRequest(accessToken);
      }

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result?.success) {
        throw new Error(result?.error || `Failed to get a reply from ${participant.errorLabel}`);
      }
    } catch (error: unknown) {
      console.error('[MessagesModal] Failed to invoke specialised AI participant:', error);
      notify.error(error instanceof Error ? error.message : `Failed to get a reply from ${participant.errorLabel}`, 3000);
    } finally {
      setAiParticipantThinkingThreadId((current) => current === threadId ? null : current);
    }
  }, []);

  const getAiThreadContext = useCallback((thread: ClientThread | undefined, content: string): AiThreadContext | null => {
    const trimmedContent = typeof content === 'string' ? content.trim() : '';

    if (isAiOperationsManagerThread(thread)) {
      const aiInvocation = getAiOperationsManagerPromptFromMessage(content);
      const shouldAutoInvokeAi = !aiInvocation.isInvocation && trimmedContent.length > 0;
      return {
        prompt: aiInvocation.prompt || (shouldAutoInvokeAi ? trimmedContent : null),
        shouldRespond: aiInvocation.shouldRespond || shouldAutoInvokeAi,
        participant: AI_OPERATIONS_PARTICIPANT_OPTION,
      };
    }

    if (isAiProposalManagerThread(thread)) {
      const aiInvocation = getAiProposalManagerPromptFromMessage(content);
      const shouldAutoInvokeAi = !aiInvocation.isInvocation && trimmedContent.length > 0;
      return {
        prompt: aiInvocation.prompt || (shouldAutoInvokeAi ? trimmedContent : null),
        shouldRespond: aiInvocation.shouldRespond || shouldAutoInvokeAi,
        participant: AI_PROPOSAL_PARTICIPANT_OPTION,
      };
    }

    return null;
  }, []);
  
  // Sync offline messages when online
  const syncOfflineMessages = useCallback(async () => {
    if (!isOnline || !user?.id) return;
    
    console.log('[MessagesModal] Syncing offline messages...');
    
    await offlineMessages.syncPendingMessages(
      async (msg: OfflineQueueMessage) => {
        const offlineThread = threads.find((thread) => thread.id === msg.thread_id || thread.thread_id === msg.thread_id);
        const aiThreadContext = getAiThreadContext(offlineThread, msg.content);

        if (!isConnected) {
          const sentMsg = await sendMessageHTTP(msg.thread_id, msg.content, {
            attachments: msg.attachments,
            replyTo: msg.reply_to_id ? { message_id: msg.reply_to_id, content: '', sender_name: '' } : undefined,
            mentions: msg.mentions,
            tempId: msg.temp_message_id,
          });

          if (aiThreadContext?.shouldRespond && aiThreadContext.prompt && offlineThread) {
            notify.info(`${aiThreadContext.participant.senderName} is reviewing the thread…`, 2500);
            void invokeAiThreadParticipant(offlineThread, aiThreadContext.prompt, aiThreadContext.participant);
          }

          return { messageId: sentMsg.id || sentMsg._id || msg.temp_message_id };
        }

        // Send via WebSocket
        return new Promise((resolve, reject) => {
          const originalOnMessageSent = callbacksRef.current.onMessageSent;
          const timeout = setTimeout(() => {
            callbacksRef.current.onMessageSent = originalOnMessageSent;
            reject(new Error('Send timeout'));
          }, 10000);
          
          // Listen for message sent response
          callbacksRef.current.onMessageSent = (threadId: string, sentMsg: ThreadMessage) => {
            if (sentMsg.id === msg.temp_message_id || (sentMsg as ThreadMessage & { temp_id?: string }).temp_id === msg.temp_message_id) {
              clearTimeout(timeout);
              callbacksRef.current.onMessageSent = originalOnMessageSent;

              if (aiThreadContext?.shouldRespond && aiThreadContext.prompt && offlineThread) {
                notify.info(`${aiThreadContext.participant.senderName} is reviewing the thread…`, 2500);
                void invokeAiThreadParticipant(offlineThread, aiThreadContext.prompt, aiThreadContext.participant);
              }

              resolve({ messageId: sentMsg.id || sentMsg._id || msg.temp_message_id });
            }
            originalOnMessageSent?.(threadId, sentMsg);
          };

          const sent = sendMessage(msg.thread_id, msg.content, {
            attachments: msg.attachments,
            replyTo: msg.reply_to_id ? { message_id: msg.reply_to_id, content: '', sender_name: '' } : undefined,
            mentions: msg.mentions,
            tempId: msg.temp_message_id
          });

          if (!sent) {
            clearTimeout(timeout);
            callbacksRef.current.onMessageSent = originalOnMessageSent;
            reject(new Error('Messaging connection unavailable'));
          }
        });
      },
      (synced, total, failed) => {
        console.log(`[MessagesModal] Sync progress: ${synced}/${total}, ${failed} failed`);
      }
    );
    
    // Update counts
    const pending = await offlineMessages.getPendingCount();
    const failed = await offlineMessages.getFailedCount();
    setPendingCount(pending);
    setFailedCount(failed);
  }, [getAiThreadContext, invokeAiThreadParticipant, isConnected, isOnline, sendMessage, sendMessageHTTP, threads, user?.id]);

  useEffect(() => {
    syncOfflineMessagesRef.current = syncOfflineMessages;
  }, [syncOfflineMessages]);
  
  // Fetch threads on mount and when connected
  useEffect(() => {
    if (isOpen && isConnected) {
      console.log('[MessagesModal] 📤 Requesting threads via getThreads()...');
      getThreads();
    }
  }, [isOpen, isConnected, getThreads]);
  
  // Handle initial thread/job/schedule ID
  useEffect(() => {
    if (!isOpen || !isConnected) return;
    
    // Skip if we've already opened this initial thread
    if (initialThreadId && initialThreadOpenedRef.current === initialThreadId) {
      return;
    }
    
    if (initialThreadId) {
      // Check if another instance is already handling this thread (cold-start protection)
      const handlingFlag = localStorage.getItem('coldstart_thread_handling');
      if (handlingFlag) {
        try {
          const { threadId, timestamp } = JSON.parse(handlingFlag);
          const age = Date.now() - timestamp;
          if (threadId === initialThreadId && age < 30000) {
            console.log('[MessagesModal] Another instance already handling thread:', initialThreadId);
            initialThreadOpenedRef.current = initialThreadId;
            return;
          } else if (age >= 30000) {
            // Stale flag, clear it
            localStorage.removeItem('coldstart_thread_handling');
          }
        } catch (e) {
          localStorage.removeItem('coldstart_thread_handling');
        }
      }
      
      // Set persistent flag to prevent duplicate handling
      localStorage.setItem('coldstart_thread_handling', JSON.stringify({
        threadId: initialThreadId,
        timestamp: Date.now()
      }));
      
      const thread = threads.find(t => t.id === initialThreadId || t.thread_id === initialThreadId);
      if (thread) {
        // Found the thread in the list - select it
        console.log('[MessagesModal] Found initial thread in list:', initialThreadId);
        initialThreadOpenedRef.current = initialThreadId;
        selectThread(thread);
        joinThread(initialThreadId);
        getThreadMessages(initialThreadId);
        setViewMode('thread');
        
        // Clear the FCM notification for this thread and mark as read
        import('@/lib/capacitorDeviceService').then(({ capacitorDeviceService }) => {
          capacitorDeviceService.cancelNotificationForThread(initialThreadId).catch(() => {});
        }).catch(() => {});
        markAsRead(initialThreadId);
        
        // Hide cold start navigation UI if showing
        if (typeof window !== 'undefined') {
          window.__hideNavigationUI?.();
          sessionStorage.removeItem('pending_message_navigation');
          localStorage.removeItem('deeplink_pending_message_navigation');
        }
        
        // Clear handling flag after successful handling
        setTimeout(() => {
          localStorage.removeItem('coldstart_thread_handling');
        }, 1000);
      } else if (threads.length > 0) {
        // Thread not in list but we have threads loaded - try to join directly
        // This handles cases where the thread exists but isn't in the initial list
        console.log('[MessagesModal] Thread not in list, joining directly:', initialThreadId);
        initialThreadOpenedRef.current = initialThreadId;
        joinThread(initialThreadId);
        getThreadMessages(initialThreadId);
        setViewMode('thread');
        
        // Clear the FCM notification for this thread and mark as read
        import('@/lib/capacitorDeviceService').then(({ capacitorDeviceService }) => {
          capacitorDeviceService.cancelNotificationForThread(initialThreadId).catch(() => {});
        }).catch(() => {});
        markAsRead(initialThreadId);
        
        // Hide cold start navigation UI if showing
        if (typeof window !== 'undefined') {
          window.__hideNavigationUI?.();
          sessionStorage.removeItem('pending_message_navigation');
          localStorage.removeItem('deeplink_pending_message_navigation');
        }
        
        // Clear handling flag after successful handling
        setTimeout(() => {
          localStorage.removeItem('coldstart_thread_handling');
        }, 1000);
      }
    } else if (initialJobId) {
      // Find or create job thread
      const jobThread = threads.find(t => t.job_id === initialJobId);
      if (jobThread) {
        const threadId = jobThread.id || jobThread.thread_id;
        selectThread(jobThread);
        joinThread(threadId);
        getThreadMessages(threadId);
        setViewMode('thread');
        
        // Clear the FCM notification for this thread and mark as read
        import('@/lib/capacitorDeviceService').then(({ capacitorDeviceService }) => {
          capacitorDeviceService.cancelNotificationForThread(threadId).catch(() => {});
        }).catch(() => {});
        markAsRead(threadId);
      }
    } else if (initialScheduleId) {
      // Find or create schedule thread
      const scheduleThread = threads.find(t => t.schedule_id === initialScheduleId);
      if (scheduleThread) {
        const threadId = scheduleThread.id || scheduleThread.thread_id;
        selectThread(scheduleThread);
        joinThread(threadId);
        getThreadMessages(threadId);
        setViewMode('thread');
        
        // Clear the FCM notification for this thread and mark as read
        import('@/lib/capacitorDeviceService').then(({ capacitorDeviceService }) => {
          capacitorDeviceService.cancelNotificationForThread(threadId).catch(() => {});
        }).catch(() => {});
        markAsRead(threadId);
      }
    }
  }, [isOpen, isConnected, threads, initialThreadId, initialJobId, initialScheduleId, joinThread, getThreadMessages, selectThread, markAsRead]);
  
  // Select a thread
  const handleSelectThread = useCallback((thread: ClientThread) => {
    const threadId = thread.id || thread.thread_id;
    haptics.selection(); // Haptic feedback when selecting thread
    selectThread(thread);
    const joined = joinThread(threadId);
    const requestedMessages = getThreadMessages(threadId); // Fetch messages for this thread
    if (!joined || !requestedMessages) {
      void fetchMessagesHTTP(threadId);
    }
    setViewMode('thread');
    
    // Clear the FCM notification for this thread (WhatsApp-style behavior)
    // When user opens a thread, dismiss any pending notification for it
    import('@/lib/capacitorDeviceService').then(({ capacitorDeviceService }) => {
      capacitorDeviceService.cancelNotificationForThread(threadId).catch(() => {
        // Silently fail - not critical
      });
    }).catch(() => {
      // capacitorDeviceService not available (web)
    });
    
    // Also mark the thread as read via WebSocket
    markAsRead(threadId);
  }, [fetchMessagesHTTP, selectThread, joinThread, getThreadMessages, markAsRead]);
  
  // Listen for direct-message shortcut events from other UI surfaces.
  useEffect(() => {
    const handleOpenDirect = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail?.userId || !isOpen) return;
      
      console.log('[MessagesModal] 📨 openDirectMessage received:', detail);
      
      // Find a direct thread with this user
      const directThread = threads.find(t => 
        t.thread_type === 'direct' && 
        t.members?.some(m => m.user_id === detail.userId)
      );
      
      if (directThread) {
        setThreadCategory('direct');
        handleSelectThread(directThread);
      } else {
        setThreadCategory('direct');
        setViewMode('list');
      }
    };
    
    window.addEventListener('openDirectMessage', handleOpenDirect);
    return () => window.removeEventListener('openDirectMessage', handleOpenDirect);
  }, [isOpen, threads, handleSelectThread]);

  // Deep-link into a direct thread by user id (from `openMessagesModal` detail.userId
  // forwarded via Header → initialDirectUserId). Runs once per value once threads load.
  useEffect(() => {
    if (!initialDirectUserId) {
      initialDirectUserHandledRef.current = null;
      return;
    }
    if (!isOpen) return;
    if (initialDirectUserHandledRef.current === initialDirectUserId) return;
    if (threads.length === 0) return; // wait for the thread list to hydrate

    initialDirectUserHandledRef.current = initialDirectUserId;
    const directThread = threads.find(t =>
      t.thread_type === 'direct' &&
      t.members?.some(m => m.user_id === initialDirectUserId)
    );

    if (directThread) {
      setThreadCategory('direct');
      handleSelectThread(directThread);
    } else {
      setThreadCategory('direct');
      setViewMode('list');
    }
  }, [isOpen, initialDirectUserId, threads, handleSelectThread]);
  
  // Listen for team-chat shortcut events from other UI surfaces.
  useEffect(() => {
    const handleOpenTeam = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail?.teamId || !isOpen) return;
      
      console.log('[MessagesModal] 👥 openTeamChat received:', detail);
      
      // Find the team thread
      const teamThread = threads.find(t => 
        (t.thread_type === 'team' || t.is_team_thread) && 
        t.team_id === String(detail.teamId)
      );
      
      if (teamThread) {
        setThreadCategory('teams');
        handleSelectThread(teamThread);
      } else {
        setThreadCategory('teams');
        setViewMode('list');
      }
    };
    
    window.addEventListener('openTeamChat', handleOpenTeam);
    return () => window.removeEventListener('openTeamChat', handleOpenTeam);
  }, [isOpen, threads, handleSelectThread]);

  // Listen for messageCentreThreadChange events (from capacitorDeviceService
  // when user taps another message notification while messages modal is open —
  // this event switches threads without reloading)
  useEffect(() => {
    const handleMessageCentreThreadChange = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail?.threadId || !isOpen) return;

      console.log('[MessagesModal] 📬 messageCentreThreadChange received:', detail.threadId);

      const thread = threads.find(t => t.id === detail.threadId || t.thread_id === detail.threadId);
      if (thread) {
        handleSelectThread(thread);
      } else {
        // Thread not in loaded list — join directly
        const threadId = detail.threadId as string;
        joinThread(threadId);
        getThreadMessages(threadId);
        setViewMode('thread');
        markAsRead(threadId);
      }
    };

    window.addEventListener('messageCentreThreadChange', handleMessageCentreThreadChange);
    return () => window.removeEventListener('messageCentreThreadChange', handleMessageCentreThreadChange);
  }, [isOpen, threads, handleSelectThread, joinThread, getThreadMessages, markAsRead]);

  // Go back to list
  const handleBack = useCallback(() => {
    haptics.light();
    selectThread(null);
    setViewMode('list');
  }, [selectThread]);

  // Refs for back handler to access current state without stale closures
  const viewModeRef = useRef(viewMode);
  const isOpenRef = useRef(isOpen);
  const selectedThreadRef = useRef(selectedThread);
  
  useEffect(() => {
    viewModeRef.current = viewMode;
    isOpenRef.current = isOpen;
    selectedThreadRef.current = selectedThread;
  }, [viewMode, isOpen, selectedThread]);
  
  // Dispatch events when modal opens/closes (for GlobalMessageNotifier)
  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('messagesModalOpened'));
    } else {
      window.dispatchEvent(new CustomEvent('messagesModalClosed'));
    }
  }, [isOpen]);
  
  // Register hardware back button handler with ModalStateContext
  useEffect(() => {
    if (!isOpen) return;
    
    // Back handler for hardware back button (Android)
    // Returns true if it handled the back press, false otherwise
    const backHandler = (): boolean => {
      // Only handle if modal is open
      if (!isOpenRef.current) return false;
      
      console.log('[MessagesModal] 🔙 Hardware back pressed:', {
        viewMode: viewModeRef.current,
        hasSelectedThread: !!selectedThreadRef.current,
      });
      
      // Priority 1: If viewing a thread, go back to list
      if (viewModeRef.current === 'thread' && selectedThreadRef.current) {
        console.log('[MessagesModal] 🔙 Going back to thread list');
        selectThread(null);
        setViewMode('list');
        return true;
      }
      
      // Priority 2: If in list view, switch to calls tab (not just close)
      if (viewModeRef.current === 'list') {
        if (onShowDashboard) {
          console.log('[MessagesModal] 🔙 Returning to dashboard shell');
          onShowDashboard();
        } else {
          onClose();
        }
        return true;
      }
      
      return false;
    };
    
    console.log('[MessagesModal] 📝 Registering back handler');
    registerBackHandler(backHandler);
    
    return () => {
      console.log('[MessagesModal] 📝 Unregistering back handler');
      unregisterBackHandler(backHandler);
    };
  }, [isOpen, onClose, onShowDashboard, selectThread, registerBackHandler, unregisterBackHandler]);
  
  // Send a message
  const handleSendMessage = useCallback(async (
    content: string, 
    attachments: Attachment[], 
    replyToId?: string,
    mentions?: number[]
  ) => {
    if (!selectedThread || !user?.id) return;

    // The messaging API rejects empty content — attachment-only sends use the
    // '📎 Attachment' placeholder (MessageBubble hides it from the bubble body)
    content = content.trim() ? content : (attachments.length > 0 ? '📎 Attachment' : content);

    const aiThreadContext = getAiThreadContext(selectedThread, content);

    if (aiThreadContext && !aiThreadContext.shouldRespond) {
      notify.error('Add a question after the AI command, or just type your message normally in this thread.', 3000);
      return;
    }
    
    const threadId = selectedThread.id || selectedThread.thread_id;
    if (!threadId) {
      console.error('[MessagesModal] No thread ID available');
      return;
    }
    
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const now = new Date().toISOString();
    
    // Optimistic update - include all required fields for proper display
    const optimisticMessage: Partial<ThreadMessage> = {
      _id: tempId,
      id: tempId,
      thread_id: threadId,
      sender_id: user.id,
      sender_user_id: user.id,
      sender_name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'You',
      content,
      content_type: attachments.length > 0 ? 'file' : 'text',
      message_type: attachments.length > 0 ? 'attachment' : 'text',
      attachments,
      reply_to_id: replyToId,
      mentions: mentions || [], // Include mentions in optimistic message
      created_at: now,
      timestamp: now, // Critical: MessageBubble uses this for display
      read_by: [],
      pending: true, // Mark as pending/optimistic
      temp_id: tempId // Store temp_id for matching server response
    };
    
    addOptimisticMessage(optimisticMessage as ThreadMessage);
    setSending(true);
    
    // Haptic feedback for message send
    haptics.light();

    const queueMessageForOffline = async (offlineError?: string) => {
      const queued = await offlineMessages.queueMessage({
        threadId,
        content,
        attachments,
        replyToId,
        tempMessageId: tempId
      });

      updateOptimisticMessage(tempId, {
        pending: true,
        offline_id: queued.offline_id,
        _offline: true,
        _offlineId: queued.offline_id,
        _offlineStatus: 'pending',
        _offlineError: offlineError
      } as Partial<ThreadMessage> & Record<string, unknown>);

      return queued;
    };
    
    // Check if online
    if (isOnline) {
      try {
        const wsSent = isConnected
          ? sendMessage(threadId, content, {
              attachments,
              replyTo: replyToId ? { message_id: replyToId, content: '', sender_name: '' } : undefined,
              mentions: mentions || [], // Pass mentions to WebSocket
              tempId
            })
          : false;

        if (!wsSent) {
          await sendMessageHTTP(threadId, content, {
            attachments,
            replyTo: replyToId ? { message_id: replyToId, content: '', sender_name: '' } : undefined,
            mentions: mentions || [],
            tempId,
          });
        }

        // Success haptic when message actually sends
        haptics.success();

        if (aiThreadContext?.shouldRespond && aiThreadContext.prompt) {
          notify.info(`${aiThreadContext.participant.senderName} is reviewing the thread…`, 2500);
          void invokeAiThreadParticipant(selectedThread, aiThreadContext.prompt, aiThreadContext.participant);
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        haptics.error();
        // Queue for offline
        await queueMessageForOffline(error instanceof Error ? error.message : 'Queued for offline sending');
      }
    } else {
      // Queue for offline sending
      await queueMessageForOffline();
    }
    
    setSending(false);
  }, [selectedThread, user, isConnected, isOnline, sendMessage, sendMessageHTTP, addOptimisticMessage, updateOptimisticMessage, getAiThreadContext, invokeAiThreadParticipant]);
  
  // Upload attachment to cloud storage
  const handleUploadAttachment = useCallback(async (file: File): Promise<Attachment | null> => {
    if (!client?.id || !user?.id) return null;
    
    setUploading(true);
    
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        throw new Error('Not authenticated');
      }
      
      // Get thread ID for reference
      const threadId = selectedThread?.id || selectedThread?.thread_id || '';
      
      // Upload to cloud storage via the unified API
      const result = await uploadToThreadStorage(file, {
        clientId: client.id,
        userId: user.id,
        threadId: threadId,
        type: 'thread',
        retentionDays: 365,
        accessToken: tokens.access_token,
      });
      
      if (!result.success || !result.url) {
        throw new Error(result.error || 'Upload failed');
      }
      
      const attachment: Attachment = {
        id: result.fileId || result.token || `att_${Date.now()}`,
        url: result.directUrl || result.url,
        originalFilename: result.originalFilename || file.name,
        contentType: result.mimeType || file.type,
        mimeType: result.mimeType || file.type,
        size: result.size || file.size,
        isImage: file.type.startsWith('image/'),
        isVideo: file.type.startsWith('video/'),
        thumbnailUrl: undefined // Cloud storage generates thumbnails on demand
      };
      
      console.log('[MessagesModal] Uploaded attachment to cloud storage:', { 
        originalFilename: file.name, 
        url: attachment.url, 
        fileId: result.fileId,
        isImage: attachment.isImage 
      });
      
      return attachment;
    } catch (error: unknown) {
      console.error('Upload error:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to upload file');
      return null;
    } finally {
      setUploading(false);
    }
  }, [client?.id, user?.id, selectedThread]);
  
  // Handle reaction
  const handleReaction = useCallback((messageId: string, emoji: string) => {
    if (!selectedThread) return;
    
    const threadId = selectedThread.id || selectedThread.thread_id;
    send({
      type: 'add_message_reaction',
      thread_id: threadId,
      message_id: messageId,
      emoji: emoji
    });
    
    // Optimistically update the local message state
    setMessages(prev => prev.map(msg => {
      if (getThreadMessageIdentifier(msg as ThreadMessage & { message_id?: string }) === messageId) {
        const existingReactions = msg.reactions || [];
        const existingReaction = existingReactions.find((reaction: MessageReaction) => reaction.emoji === emoji && reaction.user_id === user?.id);
        
        if (existingReaction) {
          return {
            ...msg,
            reactions: existingReactions.filter((reaction: MessageReaction) => !(reaction.emoji === emoji && reaction.user_id === user?.id))
          };
        }
        
        return {
          ...msg,
          reactions: [...existingReactions, {
            emoji,
            user_id: user?.id || 0,
            user_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || 'You',
            created_at: new Date().toISOString(),
          }]
        };
      }
      return msg;
    }));
  }, [selectedThread, send, setMessages, user?.id, user?.email, user?.first_name, user?.last_name]);
  
  // Handle edit message
  const handleEditMessage = useCallback((messageId: string, newContent: string) => {
    if (!selectedThread) return;
    
    const threadId = selectedThread.id || selectedThread.thread_id;
    send({
      type: 'edit_message',
      thread_id: threadId,
      message_id: messageId,
      content: newContent
    });
    
    // Optimistically update the message in local state
    // The server should broadcast the update back
  }, [selectedThread, send]);
  
  // Handle delete message
  const handleDeleteMessage = useCallback((messageId: string) => {
    if (!selectedThread) return;
    
    const threadId = selectedThread.id || selectedThread.thread_id;
    send({
      type: 'delete_message',
      thread_id: threadId,
      message_id: messageId
    });
    
    // Optimistically remove from local state if needed
    // The server should broadcast the deletion back
  }, [selectedThread, send]);

  const handleReviewAction = useCallback(async (
    message: ThreadMessage,
    action: 'acknowledge' | 'ai_hold' | 'resume_review' | 'resolve',
  ): Promise<{ review_status?: string; appointment_status?: string } | void> => {
    if (!selectedThread) return;

    const reviewItemId = message.metadata?.review_item_id;
    if (!reviewItemId) {
      notify.error('This review message is missing a review reference', 3000);
      return;
    }

    const token = authService.getTokens()?.access_token;
    if (!token) {
      notify.error('You need to be signed in to manage this review', 3000);
      return;
    }

    const response = await fetch(`/api/v1/work-items/${reviewItemId}/operations-review`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action,
        team_id: selectedThread.team_id || message.metadata?.team_id || null,
      }),
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result?.success) {
      notify.error(result?.error || 'Failed to update operations review', 3000);
      throw new Error(result?.error || 'Failed to update operations review');
    }

    notify.success(
      result?.success_message
        || (action === 'acknowledge'
          ? 'Review acknowledged'
          : action === 'ai_hold'
            ? 'Review placed on AI hold'
            : action === 'resume_review'
              ? 'Review moved back to AI review'
              : 'Review accepted')
    );

    const threadId = selectedThread.id || selectedThread.thread_id;
    if (threadId) {
      getThreadMessages(threadId);
    }

    return {
      review_status: result.review_status,
      appointment_status: result.appointment_status,
    };
  }, [selectedThread, getThreadMessages]);
  
  // Thread settings handlers
  const handleMuteThread = useCallback(() => {
    if (!selectedThread) return;
    
    const threadId = selectedThread.id || selectedThread.thread_id;
    send({
      type: 'mute_thread',
      thread_id: threadId,
      muted: !selectedThread.is_muted
    });
    
    notify.info(selectedThread.is_muted ? 'Thread unmuted' : 'Thread muted');
  }, [selectedThread, send]);
  
  const handleArchiveThread = useCallback(() => {
    if (!selectedThread) return;
    
    const threadId = selectedThread.id || selectedThread.thread_id;
    send({
      type: 'archive_thread',
      thread_id: threadId
    });
    
    notify.info('Thread archived');
    setViewMode('list');
    selectThread(null);
  }, [selectedThread, send, selectThread]);
  
  const handleDeleteThread = useCallback(async () => {
    if (!selectedThread) return;
    
    const threadId = selectedThread.id || selectedThread.thread_id;
    const threadName = selectedThread.thread_name || 'this conversation';
    
    // Use notify.confirm for better UX
    await notify.confirm({
      title: 'Delete Thread',
      message: `Are you sure you want to delete "${threadName}"? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: () => {
        send({
          type: 'delete_client_thread',
          thread_id: threadId
        });
        
        // Optimistically remove from UI
        setViewMode('list');
        selectThread(null);
      }
    });
  }, [selectedThread, send, selectThread]);
  
  // Leave thread
  const handleLeaveThread = useCallback(async () => {
    if (!selectedThread) return;
    
    const threadId = selectedThread.id || selectedThread.thread_id;
    const threadName = selectedThread.thread_name || 'this thread';
    
    // Use notify.confirm for better UX
    await notify.confirm({
      title: 'Leave Thread',
      message: `Are you sure you want to leave "${threadName}"? You will no longer receive messages from this thread.`,
      confirmText: 'Leave',
      cancelText: 'Cancel',
      variant: 'warning',
      onConfirm: () => {
        send({
          type: 'leave_client_thread',
          thread_id: threadId
        });
        
        // Optimistically remove from UI
        setViewMode('list');
        selectThread(null);
      }
    });
  }, [selectedThread, send, selectThread]);
  
  // Share location
  const handleShareLocation = useCallback(async () => {
    if (!selectedThread) {
      return;
    }
    
    setSharingLocation(true);
    
    try {
      const location = await getMessagingSharedLocation();
      const threadId = selectedThread.id || selectedThread.thread_id;
      const content = `📍 ${location.label}`;
      const locationPayload = {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        address: location.address,
      };

      const wsSent = isConnected
        ? sendMessage(threadId, content, {
            messageType: 'location',
            location: locationPayload,
          })
        : false;

      if (!wsSent) {
        await sendMessageHTTP(threadId, content, {
          messageType: 'location',
          location: locationPayload,
        });
      }

      notify.success('Location shared');
    } catch (error: unknown) {
      console.error('Location error:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to get location');
    } finally {
      setSharingLocation(false);
    }
  }, [isConnected, selectedThread, sendMessage, sendMessageHTTP]);
  
  // Send voice note
  const handleSendVoiceNote = useCallback(async (blob: Blob, duration: number) => {
    if (!selectedThread) return;
    
    // Upload the voice note
    const file = new File([blob], `voice_${Date.now()}.webm`, { type: 'audio/webm' });
    const attachment = await handleUploadAttachment(file);
    
    if (attachment) {
      attachment.duration = duration;
      attachment.contentType = attachment.contentType || attachment.mimeType || file.type;
      attachment.mimeType = attachment.mimeType || attachment.contentType;

      const threadId = selectedThread.id || selectedThread.thread_id;
      const content = '🎤 Voice note';
      const wsSent = isConnected
        ? sendMessage(threadId, content, {
            attachments: [attachment],
            messageType: 'voice_note',
          })
        : false;

      if (!wsSent) {
        await sendMessageHTTP(threadId, content, {
          attachments: [attachment],
          messageType: 'voice_note',
        });
      }
    }
  }, [isConnected, selectedThread, handleUploadAttachment, sendMessage, sendMessageHTTP]);
  
  // Handle typing
  const handleTyping = useCallback(() => {
    if (selectedThread) {
      sendTyping(selectedThread.id || selectedThread.thread_id);
    }
  }, [selectedThread, sendTyping]);
  
  // Create new thread
  const handleCreateThread = useCallback((
    type: 'direct' | 'group' | 'job' | 'schedule',
    memberIds: number[],
    title?: string,
    description?: string,
    jobId?: number,
    scheduleId?: number
  ) => {
    // Create thread with name (use title or generate one)
    const threadName = title || (type === 'direct' ? 'Direct Message' : 'New Group');
    createThread(threadName, description, memberIds);
    setShowNewThreadModal(false);
  }, [createThread]);
  
  // Open new thread modal and fetch eligible members from module-access API
  // (same API as OperationsCallModal uses for contacts)
  const handleOpenNewThreadModal = useCallback(async () => {
    setShowNewThreadModal(true);
    setLoadingOperationsUsers(true);
    
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        console.error('[MessagesModal] No access token available');
        setLoadingOperationsUsers(false);
        return;
      }
      
      // Use the same API as OperationsCallModal
      const response = await fetch('/api/v1/module-access/users?module_key=total-operations&include_operations_role=true', {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Transform to EligibleMember format and filter out current user
        const members: EligibleMember[] = data.data
          .filter((u: OperationsModuleUser) => u.id !== user?.id)
          .map((u: OperationsModuleUser) => ({
            user_id: u.id,
            first_name: u.first_name || '',
            last_name: u.last_name || '',
            email: u.email,
            avatar_url: u.avatar_url,
            display_name: u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
            modules: ['total-operations']
          }));
        setOperationsUsers(members);
      } else {
        console.error('[MessagesModal] API returned error:', data.error);
      }
    } catch (err) {
      console.error('[MessagesModal] Error fetching operations users:', err);
    } finally {
      setLoadingOperationsUsers(false);
    }
  }, [user?.id]);
  
  // Don't render if not open
  if (!isOpen) return null;

  const showTopSafeAreaSpacer = isFullPage && isMobileApp;
  // Mobile/Capacitor: raised above CapacitorFooter (z-[90]) — the modal owns the full screen.
  const mobileBackdropZIndexClass = isMobileApp ? 'z-[99]' : 'z-[89]';
  const mobilePanelZIndexClass = isMobileApp ? 'z-[100]' : 'z-[90]';

  return (
    <>

      {/* Backdrop overlay */}
      {!isFullPage && (
        <div 
          className={`fixed inset-0 bg-black/30 backdrop-blur-sm transition-all duration-300 ${mobileBackdropZIndexClass}`}
          onClick={onClose}
        />
      )}
      
      {/* Panel - slide in from right like old modal */}
      {/* When isFullPage, use fixed position with safe area handling for mobile */}
      <div data-tour-id="myconnect-messages-modal" 
        className={`${isFullPage 
          ? `fixed inset-x-0 ${mobilePanelZIndexClass}` 
          : `fixed ${mobilePanelZIndexClass} animate-slide-in-right right-0 w-full max-w-5xl shadow-2xl`
        } flex flex-col ${
          isDark ? 'bg-gray-900' : 'bg-white'
        }`}
        style={{
          ...(isFullPage && isMobileApp ? {
            top: '0px',
            bottom: 'var(--safe-area-inset-bottom)',
            transition: 'top 300ms ease',
          } : {
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            ...(!isFullPage && {
              top: 'var(--safe-area-inset-top)',
              bottom: 'var(--safe-area-inset-bottom)',
              height: 'calc(100% - var(--safe-area-inset-top) - var(--safe-area-inset-bottom))'
            }),
            ...(isFullPage && !isMobileApp && { top: 0, bottom: 0 })
          })
        }}
      >
        {showTopSafeAreaSpacer && (
          <div
            aria-hidden="true"
            className={`flex-shrink-0 ${isDark ? 'bg-gray-800' : 'bg-white'}`}
            style={{ height: MOBILE_SAFE_AREA_TOP }}
          />
        )}

        {/* Header — shared centered-icon + filter-bar modal chrome */}
        {viewMode === 'list' && (
          <CapacitorModalFilterHeader
            icon={MessageSquare}
            title="Messenger"
            isDark={isDark}
            onClose={onClose}
            subtitle={messagingStatusSummary}
            leadingActions={
              <button
                type="button"
                onClick={() => void handleMessagesRefresh()}
                className={messagesHeaderActionClass}
                title="Refresh"
                aria-label="Refresh"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            }
            filterBar={
              <ModalFilterBar
                isDark={isDark}
                search={{
                  value: searchQuery,
                  onChange: handleThreadSearchChange,
                  placeholder: 'Search conversations...',
                }}
                filters={{
                  options: threadCategoryFilter.options.map((opt) => ({
                    value: opt.key,
                    label: opt.count !== undefined && opt.count > 0 ? `${opt.label} (${opt.count > 99 ? '99+' : opt.count})` : opt.label,
                  })),
                  value: threadCategoryFilter.value,
                  onChange: threadCategoryFilter.onChange,
                  dropdownLabel: 'Filter conversations',
                }}
                sort={{
                  options: [
                    { value: 'newest', label: 'Newest first' },
                    { value: 'oldest', label: 'Oldest first' },
                    { value: 'name', label: 'Name (A-Z)' },
                    { value: 'unread', label: 'Unread first' },
                  ],
                  value: sortBy,
                  onChange: (v) => setSortBy(v as typeof sortBy),
                }}
                onCreateNew={handleOpenNewThreadModal}
                createLabel="New conversation"
                createTourId="myconnect-messages-new-thread"
              />
            }
          />
        )}
        
        {/* Main content area */}
        <div data-tour-id="myconnect-messages-content" className="flex-1 flex flex-col min-h-0">
          {/* Share mode banner */}
          {isShareMode && viewMode === 'list' && (
            <div className={`px-4 py-3 border-b flex items-center gap-3 ${
              isDark ? 'bg-blue-900/30 border-blue-800 text-blue-200' : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}>
              <div className="flex-shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">Share to Message</p>
                <p className="text-xs opacity-75 truncate">
                  {shareAttachments.length > 0 
                    ? `${shareAttachments.length} file${shareAttachments.length > 1 ? 's' : ''} ready to share`
                    : shareText 
                      ? 'Text ready to share'
                      : 'Select a thread to share'}
                </p>
              </div>
              <button
                onClick={() => {
                  setIsShareMode(false);
                  setShareAttachments([]);
                  setShareText('');
                  onClose();
                }}
                className={`p-1.5 rounded-full transition-colors ${
                  isDark ? 'hover:bg-blue-800' : 'hover:bg-blue-100'
                }`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {/* Thread list (show on list view only - no sidebar on mobile isFullPage mode) */}
          {viewMode === 'list' && (
            <div className="flex-1 overflow-auto">
              <ThreadList
                threads={filteredThreads}
                selectedThread={selectedThread}
                onSelectThread={handleSelectThread}
                loading={threads.length === 0 && (loading || connectionStatus === 'connecting' || connectionStatus === 'reconnecting')}
                searchQuery={searchQuery}
                onSearchChange={handleThreadSearchChange}
                sortBy={sortBy}
                onSortChange={setSortBy}
                isSearching={isSearching}
                onClearSearch={handleClearThreadSearch}
                totalClientsInThreads={totalClientsInThreads}
                typingUsers={new Map(selectedThread ? [[selectedThread.thread_id, currentTypingUsers]] : [])}
                currentUserId={user?.id}
                clientLogo={clientBranding?.logo_url}
                categoryFilter={threadCategoryFilter}
                hideControls
              />
            </div>
          )}
          
          {/* Thread view */}
          {viewMode === 'thread' && selectedThread && (
            <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
              <ThreadView
                thread={selectedThread}
                messages={threadMessagesWithPermalinks}
                currentUserId={user?.id || 0}
                currentUserName={`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || 'You'}
                onBack={handleBack}
                onSendMessage={handleSendMessage}
                onSendVoiceNote={handleSendVoiceNote}
                onShareLocation={handleShareLocation}
                onReaction={handleReaction}
                onDeleteMessage={handleDeleteMessage}
                onEditMessage={handleEditMessage}
                onReviewAction={handleReviewAction}
                onUploadAttachment={handleUploadAttachment}
                onTyping={handleTyping}
                onStoppedTyping={() => {}}
                onAddToTasks={handleAddMessageToManagedTasks}
                hasTaskReference={hasManagedMessageTask}
                addingTaskId={addingManagedMessageTaskId}
                onMuteThread={handleMuteThread}
                onLeaveThread={handleLeaveThread}
                onDeleteThread={handleDeleteThread}
                onDismissMention={dismissMention}
                onDismissAllMentions={dismissAllMentions}
                // Team/Group call handlers
                onStartVoiceCall={() => {
                  if (selectedThread && selectedThread.members) {
                    const participantIds = selectedThread.members
                      .filter(m => m.user_id !== user?.id)
                      .map(m => m.user_id);
                    
                    // Dispatch window event for OperationsCallModal to handle
                    window.dispatchEvent(new CustomEvent('openGroupCallModal', {
                      detail: {
                        threadId: selectedThread.thread_id || selectedThread.id,
                        threadName: selectedThread.title || selectedThread.thread_name || 'Group Call',
                        callType: 'voice',
                        participantIds,
                        autoStart: true
                      }
                    }));
                  }
                }}
                onStartVideoCall={() => {
                  if (selectedThread && selectedThread.members) {
                    const participantIds = selectedThread.members
                      .filter(m => m.user_id !== user?.id)
                      .map(m => m.user_id);
                    
                    // Dispatch window event for OperationsCallModal to handle
                    window.dispatchEvent(new CustomEvent('openGroupCallModal', {
                      detail: {
                        threadId: selectedThread.thread_id || selectedThread.id,
                        threadName: selectedThread.title || selectedThread.thread_name || 'Video Call',
                        callType: 'video',
                        participantIds,
                        autoStart: true
                      }
                    }));
                  }
                }}
                onStartMeeting={(callType) => {
                  if (selectedThread && selectedThread.members) {
                    const participantIds = selectedThread.members
                      .filter(m => m.user_id !== user?.id)
                      .map(m => m.user_id);
                    
                    // Dispatch window event for OperationsCallModal to handle - meeting mode
                    window.dispatchEvent(new CustomEvent('openGroupCallModal', {
                      detail: {
                        threadId: selectedThread.thread_id || selectedThread.id,
                        threadName: selectedThread.title || selectedThread.thread_name || 'Meeting',
                        callType,
                        participantIds,
                        isMeeting: true, // This is a meeting, not a call
                        autoStart: true
                      }
                    }));
                  }
                }}
                canMakeCalls={true}
                typing={currentTypingUsers}
                pinnedMentions={pinnedMentions}
                isDark={isDark}
                sending={sending}
                uploadingAttachment={uploading}
                sharingLocation={sharingLocation}
                aiParticipantThinking={aiParticipantThinkingThreadId === (selectedThread.id || selectedThread.thread_id)}
                isFullPage={isFullPage}
                isAdmin={user?.role === 'admin' || user?.role === 'owner' || user?.role === 'root'}
                clientLogo={clientBranding?.logo_url}
                initialAttachments={isShareMode ? shareAttachments : undefined}
                initialText={isShareMode ? shareText : undefined}
                isShareMode={isShareMode}
                onShareComplete={() => {
                  console.log('[MessagesModal] 📤 Share complete, clearing share mode');
                  setIsShareMode(false);
                  setShareAttachments([]);
                  setShareText('');
                }}
              />
            </div>
          )}
        </div>
      </div>
      
      {/* New thread modal would go here */}
      {showNewThreadModal && (
        <NewThreadModal
          isOpen={showNewThreadModal}
          onClose={() => setShowNewThreadModal(false)}
          onCreateThread={handleCreateThread}
          eligibleMembers={operationsUsers}
          isLoading={loadingOperationsUsers}
          isDark={isDark}
          primaryColor={primaryColor}
          slideOverLayout={!isFullPage}
        />
      )}
    </>
  );
}

// Simple new thread modal component
interface NewThreadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateThread: (
    type: 'direct' | 'group' | 'job' | 'schedule',
    memberIds: number[],
    title?: string,
    description?: string,
    jobId?: number,
    scheduleId?: number
  ) => void;
  eligibleMembers: OperationsEligibleMember[];
  isLoading?: boolean;
  isDark: boolean;
  primaryColor: string;
  /** When true, panel is a right-aligned slide-over (desktop); when false, full viewport (mobile/native). */
  slideOverLayout?: boolean;
}

function NewThreadModal({
  isOpen,
  onClose,
  onCreateThread,
  eligibleMembers,
  isLoading,
  isDark,
  primaryColor,
  slideOverLayout = false,
}: NewThreadModalProps) {
  const [threadName, setThreadName] = useState('');
  const [threadDescription, setThreadDescription] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [search, setSearch] = useState('');
  
  // Filter by search (members already filtered by module access from API)
  const filteredMembers = eligibleMembers.filter(m =>
    m.display_name?.toLowerCase().includes(search.toLowerCase()) ||
    m.email?.toLowerCase().includes(search.toLowerCase())
  );
  const selectedRecipients = eligibleMembers.filter((member) => selectedMembers.includes(member.user_id));
  const isGroupThread = selectedMembers.length > 1;
  const directRecipient = selectedRecipients[0];
  const autoThreadName = directRecipient?.display_name || directRecipient?.email || 'Direct Message';
  const canCreate = selectedMembers.length === 1 || (isGroupThread && threadName.trim().length > 0);
  const selectionSummary = selectedMembers.length === 0
    ? 'Select one person for a direct chat or multiple people for a group thread'
    : selectedMembers.length === 1
      ? `Direct conversation with ${autoThreadName}`
      : `${selectedMembers.length} recipients selected · group thread`;
  
  const toggleMember = (userId: number) => {
    setSelectedMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };
  
  const handleCreate = () => {
    if (!canCreate || selectedMembers.length === 0) return;

    const nextThreadName = isGroupThread
      ? threadName.trim()
      : autoThreadName;
    
    onCreateThread(
      isGroupThread ? 'group' : 'direct',
      selectedMembers,
      nextThreadName,
      isGroupThread ? (threadDescription.trim() || undefined) : undefined,
      undefined,
      undefined
    );
    
    // Reset
    setThreadName('');
    setThreadDescription('');
    setSelectedMembers([]);
    setSearch('');
  };
  
  if (!isOpen) return null;

  const panelSurface = `${isDark ? 'bg-gray-900' : 'bg-white'}`;

  const slidePanelStyle: React.CSSProperties = {
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)',
    top: 'var(--safe-area-inset-top)',
    bottom: 'var(--safe-area-inset-bottom)',
    height: 'calc(100% - var(--safe-area-inset-top) - var(--safe-area-inset-bottom))',
  };

  const fullBleedPanelStyle: React.CSSProperties = {
    paddingTop: 'env(safe-area-inset-top, 0px)',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  };

  const panel = (
    <div
      className={`flex flex-col shadow-2xl animate-slide-in-right ${panelSurface} ${
        slideOverLayout
          ? 'fixed z-[100] right-0 w-full max-w-5xl'
          : 'fixed inset-0 z-[100] w-full max-w-full'
      }`}
      style={slideOverLayout ? slidePanelStyle : fullBleedPanelStyle}
    >
      <div className={`relative flex-1 flex flex-col ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
        {/* Header - SmartClock/SmartTask style */}
        <div className={`border-b shadow-sm flex-shrink-0 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          {/* Theme-colored accent bar */}
          <div className="h-0.5" style={{ backgroundColor: primaryColor }} />

          <div className="max-w-4xl mx-auto px-3 py-1.5 flex items-center gap-2">
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
              title="Back"
            >
              <ChevronLeft className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>

            {/* Icon pill */}
            <div className="p-1 rounded-md flex-shrink-0" style={{ backgroundColor: `${primaryColor}15` }}>
              <MessageSquare className="w-4 h-4" style={{ color: primaryColor }} />
            </div>

            {/* Title */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-center">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {isGroupThread ? 'New Group Thread' : 'New Message'}
                </span>
              </div>
              <div className="flex items-center justify-center gap-1">
                <span className="text-[10px] text-gray-500 dark:text-gray-400 text-center">
                  {selectionSummary}
                </span>
              </div>
            </div>

            <button 
              onClick={onClose} 
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-hidden min-h-0">
          <div className="p-4 space-y-4 overflow-y-auto h-full max-w-3xl w-full mx-auto">
          {/* Recipient selection */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Members / Recipients *
            </label>
            
            {/* Search */}
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className={`w-full px-4 py-2 rounded-lg border mb-3 ${
                isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-400'
              }`}
            />
            
            {/* Selected members chips */}
            {selectedMembers.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedMembers.map(id => {
                  const member = eligibleMembers.find(m => m.user_id === id);
                  return member && (
                    <span 
                      key={id}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                        isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {member.display_name || `${member.first_name} ${member.last_name}`.trim()}
                      <button
                        onClick={() => toggleMember(id)}
                        className="hover:opacity-70"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          
            {/* Member list */}
            <div
              className={`${slideOverLayout ? 'max-h-[min(28rem,calc(100vh-15rem))]' : 'max-h-48'} overflow-y-auto rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
            >
              {isLoading ? (
                <div className={`p-4 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <div className="inline-block w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  Loading team members...
                </div>
              ) : filteredMembers.length > 0 ? (
                filteredMembers.map(member => {
                  const isSelected = selectedMembers.includes(member.user_id);
                  const memberOnline = isMemberOnline(member);
                  const lastSeen = getMemberLastSeen(member);
                  return (
                    <button
                      key={member.user_id}
                      onClick={() => toggleMember(member.user_id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                        isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                      } ${isSelected ? (isDark ? 'bg-gray-700' : 'bg-blue-50') : ''}`}
                    >
                      <div className="relative">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white overflow-hidden font-medium ${
                          isDark ? 'bg-gray-600' : 'bg-gray-400'
                        }`}>
                          {member.avatar_url ? (
                            <img src={member.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                          ) : (
                            member.display_name?.charAt(0)?.toUpperCase() || member.first_name?.charAt(0)?.toUpperCase() || '?'
                          )}
                        </div>
                        {/* Online indicator */}
                        {member.show_when_online !== false && (
                          <span 
                            className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 ${
                              isDark ? 'border-gray-800' : 'border-white'
                            } ${memberOnline ? 'bg-green-500' : 'bg-gray-400'}`}
                            title={lastSeen || 'Offline'}
                          />
                        )}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className={`font-medium text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {member.display_name || `${member.first_name} ${member.last_name}`.trim()}
                        </p>
                        <p className={`text-xs truncate ${
                          memberOnline 
                            ? 'text-green-500' 
                            : isDark ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {lastSeen || member.email}
                        </p>
                      </div>
                      {isSelected && (
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                      )}
                    </button>
                  );
                })
              ) : (
                <p className={`p-4 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {search ? 'No members found' : 'No team members with Total Operations access'}
                </p>
              )}
            </div>
          </div>

          {selectedMembers.length === 1 && directRecipient && (
            <div className={`rounded-lg border px-4 py-3 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
              <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Direct conversation
              </p>
              <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                This chat will be named automatically for <span className="font-medium">{autoThreadName}</span>.
              </p>
            </div>
          )}

          {isGroupThread && (
            <>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Thread Name *
                </label>
                <input
                  type="text"
                  value={threadName}
                  onChange={(e) => setThreadName(e.target.value)}
                  placeholder="e.g., Field Team, Operations"
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-400'
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
                <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  Group conversations with multiple recipients need a shared thread name.
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Description (optional)
                </label>
                <textarea
                  value={threadDescription}
                  onChange={(e) => setThreadDescription(e.target.value)}
                  placeholder="What is this thread about?"
                  rows={2}
                  className={`w-full px-4 py-2 rounded-lg border resize-none ${
                    isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-400'
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>
            </>
          )}
          </div>
        </div>
        
        <div className={`p-4 border-t mt-auto ${isDark ? 'border-gray-700 bg-gray-900/95' : 'border-gray-200 bg-white/95'} backdrop-blur-sm`}>
          <div className="max-w-3xl w-full mx-auto flex items-center gap-3">
            <button
              onClick={onClose}
              className={`flex-1 py-2.5 rounded-lg font-medium transition-colors border ${
                isDark
                  ? 'border-gray-600 text-gray-200 hover:bg-gray-800'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={!canCreate}
              className={`flex-[1.3] py-2.5 rounded-lg font-medium transition-colors ${
                !canCreate
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              {selectedMembers.length === 0
                ? 'Select recipients'
                : isGroupThread
                  ? 'Create group thread'
                  : 'Start conversation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (slideOverLayout) {
    return (
      <>
        <div
          className="fixed inset-0 z-[95] bg-black/30 backdrop-blur-sm transition-all duration-300"
          onClick={onClose}
          aria-hidden
        />
        {panel}
      </>
    );
  }

  return panel;
}
